import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Plus, Loader2, Archive } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useWriterEnabled } from '@/hooks/useWriterEnabled';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { createWriterDraft } from '@/lib/writer/createWriterDraft';
import WriterGuestLanding from '@/components/writer/WriterGuestLanding';
import WriterStage from '@/components/writer/WriterStage';
import PromptComposer from '@/components/writer/guest/PromptComposer';
import {
  PENDING_WRITER_PROMPT_KEY,
  clearPendingIntent,
  peekPendingIntent,
  storePendingIntent,
} from '@/lib/auth/pendingIntent';
import { toast } from 'sonner';

type WriterProjectRow = {
  id: string;
  title: string;
  updated_at: string;
};

/**
 * Entry point for PaperVerse Writer.
 *
 * Signed-out visitors get the prompt-first guest landing
 * (`WriterGuestLanding`). Signed-in users get the same stage and the same
 * composer, but as a workspace: type a prompt to start a draft, with the
 * existing drafts listed underneath. A prompt typed on either side is
 * stored as a pending intent and dispatched as the first agent turn by
 * `useWriterThread` once the project page loads.
 */
const WriterHome: React.FC = () => {
  const { enabled, loading: flagLoading } = useWriterEnabled();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<WriterProjectRow[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [creating, setCreating] = useState(false);
  const [replaying, setReplaying] = useState(false);
  // One replay per mount: a failed replay must never re-trigger the effect.
  const replayedRef = useRef(false);

  /** Create a project, seed v1, and open it. The pending prompt (if any)
   *  stays in sessionStorage; `useWriterThread` dispatches it once the
   *  project page loads. */
  const createProjectAndOpen = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const projectId = await createWriterDraft(user.id);
    if (!projectId) {
      toast.error('Could not create draft');
      return false;
    }
    navigate(`/write/${projectId}`);
    return true;
  }, [user, navigate]);

  // Post-auth replay: a guest prompt survived the sign-in wall. Waits for
  // auth to settle so the insert never runs against a half-hydrated
  // session. On failure the prompt is dropped so the user is not caught in
  // a retry loop, and they land on the regular project list.
  useEffect(() => {
    if (authLoading || flagLoading || !enabled || !user || replayedRef.current) return;
    if (!peekPendingIntent(PENDING_WRITER_PROMPT_KEY)) return;
    replayedRef.current = true;
    // Consume the OAuth redirect intent — this page IS the destination.
    clearPendingIntent('redirectAfterAuth');
    setReplaying(true);
    void createProjectAndOpen()
      .then((ok) => {
        if (!ok) clearPendingIntent(PENDING_WRITER_PROMPT_KEY);
      })
      .finally(() => setReplaying(false));
  }, [authLoading, flagLoading, enabled, user, createProjectAndOpen]);

  const loadProjects = useCallback(async () => {
    if (!user) return;
    setLoadingProjects(true);
    const { data, error } = await supabase
      .from('writer_projects')
      .select('id, title, updated_at')
      .eq('archived', false)
      .order('updated_at', { ascending: false })
      .limit(50);
    if (error) {
      console.error('[Writer] Failed to load projects', error);
      toast.error('Could not load Writer projects');
    } else {
      setProjects((data ?? []) as WriterProjectRow[]);
    }
    setLoadingProjects(false);
  }, [user]);

  useEffect(() => {
    if (enabled && user) loadProjects();
  }, [enabled, user, loadProjects]);

  const handleCreate = async () => {
    if (!user || creating) return;
    setCreating(true);
    await createProjectAndOpen();
    setCreating(false);
  };

  /** Composer submit: store the prompt, then create and open the draft.
   *  Identical to the guest path once the user is through the auth wall. */
  const handleComposerSubmit = async (prompt: string) => {
    if (!user || creating) return;
    replayedRef.current = true; // this IS the replay
    storePendingIntent(PENDING_WRITER_PROMPT_KEY, prompt);
    setCreating(true);
    const ok = await createProjectAndOpen();
    if (!ok) clearPendingIntent(PENDING_WRITER_PROMPT_KEY);
    setCreating(false);
  };

  const handleArchive = async (
    e: React.MouseEvent,
    project: WriterProjectRow,
  ) => {
    e.stopPropagation();
    if (!window.confirm(`Archive "${project.title}"? You can restore it from the database.`)) {
      return;
    }
    // Optimistic — remove from list, revert on error.
    const prev = projects;
    setProjects((rows) => rows.filter((r) => r.id !== project.id));
    const { error } = await supabase
      .from('writer_projects')
      .update({ archived: true })
      .eq('id', project.id);
    if (error) {
      console.error('[Writer] Archive failed', error);
      toast.error('Could not archive draft');
      setProjects(prev);
      return;
    }
    toast.success('Draft archived');
  };

  /** Same stage as the composer surface, so nothing snaps between states. */
  const stagedMessage = (node: React.ReactNode) => (
    <div className="min-h-screen bg-background">
      <WriterStage className="flex min-h-screen items-center justify-center">
        <div className="relative z-10 flex flex-col items-center gap-3 px-6 text-center">
          {node}
        </div>
      </WriterStage>
    </div>
  );

  if (flagLoading || authLoading) {
    return stagedMessage(<Loader2 className="h-6 w-6 animate-spin text-primary" />);
  }

  if (!enabled) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-24 text-center">
          <h1 className="text-2xl font-semibold mb-3">Writer is temporarily unavailable</h1>
          <p className="text-muted-foreground">PaperVerse Writer is switched off platform-wide. Please check back shortly.</p>
        </main>
        <Footer />
      </div>
    );
  }

  // Guest entry: prompt-first landing, no editor, no empty canvas.
  if (!user) {
    return <WriterGuestLanding />;
  }

  // Post-auth replay in progress: keep the user out of the project list
  // while their draft is being created.
  if (replaying) {
    return stagedMessage(
      <>
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Setting up your draft…</p>
      </>,
    );
  }

  return (
    <>
      <Helmet>
        <title>Writer · PaperVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="relative flex-1 w-full overflow-hidden">
          <WriterStage className="absolute inset-0" />

          <div className="relative mx-auto w-full max-w-3xl px-6 pt-20 pb-24">
            <h1 className="mb-3 text-center text-4xl font-semibold tracking-tight sm:text-5xl">
              What are you writing?
            </h1>
            <p className="mx-auto mb-9 max-w-[52ch] text-center text-base text-muted-foreground sm:text-lg">
              Describe it and the agent drafts it in LaTeX, compiles it, and shows you the result.
            </p>

            <PromptComposer
              onSubmitPrompt={(p) => void handleComposerSubmit(p)}
              showChips={false}
              submitLabel="Start a draft"
              idleHint="Start a new draft, or pick one up below"
              busy={creating}
            />

            {/* Recent projects */}
            <div className="mt-16 mb-4 flex items-baseline justify-between">
              <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Your drafts</h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleCreate()}
                disabled={creating}
                className="text-xs"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
                New draft
              </Button>
            </div>

            {loadingProjects ? (
              <div className="writer-surface flex items-center gap-3 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : projects.length === 0 ? (
              <div className="rounded-[1.25rem] border border-dashed border-border/60 bg-card/50 p-8 text-center text-sm text-muted-foreground">
                No drafts yet. Describe your paper above and the first one starts itself.
              </div>
            ) : (
              <ul className="writer-surface divide-y divide-border/40 overflow-hidden">
                {projects.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-2 pr-3 transition hover:bg-accent/30"
                  >
                    <button
                      onClick={() => navigate(`/write/${p.id}`)}
                      className="flex-1 text-left px-5 py-4 flex items-center justify-between gap-4"
                    >
                      <div>
                        <div className="font-medium">{p.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Updated {new Date(p.updated_at).toLocaleString()}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground/70 font-mono">
                        {p.id.slice(0, 8)}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Archive ${p.title}`}
                      onClick={(e) => void handleArchive(e, p)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <p className="mt-8 text-center text-xs text-muted-foreground">
              Your draft stays private until you publish · Written and processed in the EU
            </p>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WriterHome;
